// Implements: F-UIX-009, F-UIX-010
/**
 * The session, which is a query and not a store (design DD9).
 *
 * The application boots with ONE request, `GET /auth/me/scopes`, cached under
 * `["session","scopes"]`. There is no `GET /auth/me`: the scopes response
 * already carries the user, so a second profile call would be a second source
 * of truth for the same fact.
 *
 * The `401` of THAT request means "nobody is signed in", not "the session
 * expired". It is the one request the interceptor of `httpClient` deliberately
 * does not redirect (a named request, not a class of them), and it is also the
 * one this module turns into a value instead of an error: an anonymous browser
 * is the normal first visit, and showing it a failure would be a lie.
 *
 * A failure that outlives its two retries is NOT that `401` and must never be
 * read as one: reporting `anonymous` for a backend outage signs out somebody
 * who is in fact still signed in, and the guard then throws their route away
 * on the way to `/login`. So there is a fourth state, `unavailable`, which
 * says "we do not know" — the guard shows the outage in place, with a retry,
 * and the address bar is left exactly as it was found.
 */

import { useQuery } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { createContext, useContext, useMemo } from "react";
import type { ReactElement, ReactNode } from "react";

import type { ScopesView } from "../../types/api";
import type { ApiFailure } from "../../types/errors";
import { ApiError, UNKNOWN_ERROR_CODE } from "./ApiError";
import { API_BASE } from "./apiBase";
import { httpRequest } from "./httpClient";
import { SESSION_SCOPES_KEY } from "./queryKeys";

/** Annex §11.1: the bootstrap route, and the only profile call there is. */
export const SCOPES_PATH = `${API_BASE}/auth/me/scopes`;

/** The three states of design DD9, plus the outage none of them describes. */
export type SessionState =
  | { readonly status: "loading" }
  | { readonly status: "authenticated"; readonly view: ScopesView }
  | { readonly status: "anonymous" }
  | { readonly status: "unavailable"; readonly error: ApiFailure };

const LOADING: SessionState = { status: "loading" };
const ANONYMOUS: SessionState = { status: "anonymous" };

/** The failure shown when whatever the query threw was not an `ApiError`. */
const UNREACHABLE: ApiFailure = { status: 0, code: UNKNOWN_ERROR_CODE, message: "" };

/**
 * Fetch the scopes, turning the anonymous `401` into a value.
 *
 * @returns The view for a signed-in browser, or `null` for `401`.
 * @throws {ApiError} For any other failure, which the query records as an
 *   error and this module then reads as anonymous as well.
 */
export async function fetchScopes(): Promise<ScopesView | null> {
  try {
    return (await httpRequest<ScopesView>(SCOPES_PATH)) ?? null;
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 401) return null;
    throw cause;
  }
}

/**
 * Empty the session state and every other cached answer with it.
 *
 * The ORDER is the whole point, and it was found the hard way.
 * `queryClient.clear()` drops the cache but does not notify the observers
 * already mounted on it: a `useQuery` that was reading the session keeps
 * reporting the answer it had, so the guard still believes there is a session
 * while the cache says there is none. With `/login` sending a session back to
 * where it came from, that disagreement is an endless bounce and a blank
 * screen — which is exactly what signing out and expiring both produced.
 *
 * Writing `null` into the session key FIRST notifies that observer in the same
 * tick, so the tree is anonymous immediately. Removing everything else
 * afterwards leaves nothing behind that belonged to whoever has just left.
 *
 * @param queryClient The cache the application shares.
 */
export function emptySession(queryClient: QueryClient): void {
  queryClient.setQueryData(SESSION_SCOPES_KEY, null);
  queryClient.removeQueries({
    predicate: (query) => query.queryKey[0] !== SESSION_SCOPES_KEY[0],
  });
}

export interface SessionContextValue {
  readonly state: SessionState;
  /** Runs the bootstrap again. The only way out of `unavailable`. */
  readonly retry: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export interface SessionProviderProps {
  readonly children: ReactNode;
}

/**
 * Runs the bootstrap once and hands its outcome down.
 *
 * Once, because it is a query: every descendant reads the same cache entry
 * rather than asking again, and the whole tree learns about a new session by
 * invalidating that one key.
 */
export function SessionProvider({ children }: SessionProviderProps): ReactElement {
  const query = useQuery({ queryKey: SESSION_SCOPES_KEY, queryFn: fetchScopes });
  const { isPending, isError, error, data, refetch } = query;

  const value = useMemo<SessionContextValue>(() => {
    const retry = (): void => {
      void refetch();
    };
    if (isPending) return { state: LOADING, retry };
    if (isError) {
      const failure = error instanceof ApiError ? error : UNREACHABLE;
      return { state: { status: "unavailable", error: failure }, retry };
    }
    return { state: data == null ? ANONYMOUS : { status: "authenticated", view: data }, retry };
  }, [isPending, isError, error, data, refetch]);

  return <SessionContext value={value}>{children}</SessionContext>;
}

/** The session state, from the nearest provider. */
export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (value === null) {
    throw new Error("useSession must be called inside a SessionProvider");
  }
  return value;
}
